import qs from 'qs';
import { enums } from '@optimizely/react-sdk';
import * as helpers from './helpers';
import getUserDevice from './getUserDevice';

let optimizelyClient;
let optimizelyUserContext;

export const getOptimizelyEnvironment = () => {
  const environment = helpers.getEnvironment();
  switch (environment) {
    case 'local':
    case 'dev':
      return 'development';
    case 'staging':
      return 'staging';
    case 'prod':
      return 'production';
    default:
      return environment;
  }
};

export const getOptimizelyPageTrackingEvent = () => {
  const documentType = qs.parse(helpers.getSearch(), { ignoreQueryPrefix: true }).documentType;
  const path = helpers.getPathname();
  const page = path.split('/').pop();
  const isDocumentTypeUserUploaded = (
    page === 'edit.php' && (
      documentType.length === 32 ||
      (documentType.length === 35 && documentType.startsWith('ct-'))
    )
  );
  const documentTypeSegment = (
    documentType &&
    !isDocumentTypeUserUploaded &&
    ['builder.php', 'edit.php'].includes(page)
  ) ? `_${documentType}` : '';
  const optimizelyEnvironment = getOptimizelyEnvironment();
  const pathSegments = path === '/' ? 'home' : `${path
    .slice(1)
    .replace(/\./g, '_')
    .replace(/\//g, '_')}${documentTypeSegment}`;

  return `visit_page_${pathSegments}_${optimizelyEnvironment}`;
};

export const trackOptimizelyPageView = async () => {
  const optimizelyUserId = window.COOKIE.get('optimizelyUserId');
  if (!optimizelyUserId) {
    return Promise.resolve();
  }
  const eventName = getOptimizelyPageTrackingEvent();
  return optimizelyClient.track(eventName);
};

export const setOptimizelyForcedVariation = () => {
  const optimizelyUserId = window.COOKIE.get('optimizelyUserId');
  const { optimizelyExperiment, optimizelyVariation } = qs.parse(
    helpers.getSearch(),
    { ignoreQueryPrefix: true },
  );

  if (optimizelyVariation && optimizelyExperiment && optimizelyUserId) {
    optimizelyClient.setForcedVariation(
      optimizelyExperiment,
      optimizelyUserId,
      optimizelyVariation,
    );
  }
};

/**
 * Returns experiment key and variation key given an Optimizely decision and test type.
 * The Optimizely decision contains the experiment and variation key, however these keys
 * are retrieved differently depending on what the Optimizely test type is.
 *
 * See https://docs.developers.optimizely.com/full-stack/docs/decision-notification-listener#section-type-parameter
 * for more information on legacy api "decisionInfo" and "type" parameters
 * See https://docs.developers.optimizely.com/full-stack/v4.0/docs/decision-notification-listener
 * for more information on flags api "decisionInfo" and "type" parameters
 *
 * Parameters:
 * decisionInfo: An Optimizely decision object that contains experiment information for the user.
 * optimizelyTestType: The Optimizely test type, this can either be "flag",
 *   the only type for flags api, or it can be a legacy api type;
 *   "ab-test", "feature-test", "feature", "feature-variable", or "all-feature-variables".
 */
export const getExperimentInfoFromDecision = (decisionInfo, optimizelyTestType) => {
  let experimentKey;
  let variationKey;
  if (optimizelyTestType === 'ab-test' || optimizelyTestType === 'feature-test') {
    experimentKey = decisionInfo.experimentKey;
    variationKey = decisionInfo.variationKey;
  } else if (optimizelyTestType === 'flag') {
    experimentKey = decisionInfo.ruleKey;
    variationKey = decisionInfo.variationKey;
  } else {
    experimentKey = decisionInfo.sourceInfo.experimentKey;
    variationKey = decisionInfo.sourceInfo.variationKey;
  }
  return { experimentKey, variationKey };
};

export const onDecision = async ({ decisionInfo, type }) => {
  const { experimentKey, variationKey } = getExperimentInfoFromDecision(decisionInfo, type);

  // If there is no variation key, the user is not bucketed into the split test.
  if (!variationKey) return;

  const experimentId = optimizelyClient.getOptimizelyConfig().experimentsMap[experimentKey].id;

  if (!window.COOKIE.get('userid')) {
    const optimizelyExperimentsCookie = window.COOKIE.get('optimizelyExperiments');
    const experiments = JSON.parse(optimizelyExperimentsCookie || '{}');
    experiments[experimentKey] = { id: experimentId, variationKey };
    window.COOKIE.set('optimizelyExperiments', JSON.stringify(experiments), 365 * 5);
  }

  trackOptimizelyPageView();

  if (window.mixpanel) {
    window.mixpanel.register({
      [`${experimentKey} (${experimentId})`]: variationKey,
    });
  }
  window._track(`Experiment Decided: ${experimentKey} (${experimentId})`,
  `${variationKey}`);

  const additionDataObj = {};
  additionDataObj[`exp_${experimentId}`] = variationKey;
  window._trackAdditionalData.save(`formswift.com${helpers.getPathname()}`, additionDataObj);
};

export const getOptimizelyClient = () => {
  if (optimizelyClient) return optimizelyClient;
  optimizelyClient = helpers.createInstance();
  // Page view tracking for all experiments not decided on this page
  optimizelyClient.onReady().then(() => {
    setOptimizelyForcedVariation();
    trackOptimizelyPageView();
  });

  // Page view tracking for experiments decided on this page
  optimizelyClient.notificationCenter.addNotificationListener(
    enums.NOTIFICATION_TYPES.DECISION,
    onDecision,
  );
  return optimizelyClient;
};

/**
 * An async drop-in replacement for getOptimizelyClient that guarantees Optimizely is ready to make
 * decisions before returning the client.
 * @returns {OptimizelyReactSDKClient}
 */
export const getReadyClient = async () => {
  const client = await getOptimizelyClient();
  const result = await client.onReady();
  if (result.success) {
    return client;
  }
  throw new Error(result.reason);
};

export const clearCachedClient = () => {
  optimizelyClient = null;
  optimizelyUserContext = null;
};

export const clearCachedUserContext = () => {
  optimizelyUserContext = null;
};

export const getOrCreateOptimizelyUserIdCookie = () => {
  let optimizelyUserId = window.COOKIE.get('optimizelyUserId');
  if (!optimizelyUserId) {
    optimizelyUserId = helpers.getUuid();
    window.COOKIE.set('optimizelyUserId', optimizelyUserId);
  }
  return optimizelyUserId;
};

export const getOptimizelyUserAttributes = () => {
  const userId = window.COOKIE.get('userid');
  const optimizelyOptOut = window.COOKIE.get('optimizelyOptOut');
  const { User: { isCancelled = false } = {} } = window;
  return {
    device: getUserDevice(navigator.userAgent),
    is_cancelled: !!isCancelled,
    is_logged_in: !!userId,
    is_opted_out: !!optimizelyOptOut,
    // Conditionally set userId. Optimizely will throw an error for undefined values.
    ...(userId && { userId }),
  };
};

/**
 * Use the Javascript SDK to create an OptimizelyUserContext that can be
 * used for testing feature flags and calling decide methods directly.
 * @returns {Promise<OptimizelyUserContext>}
 */
export const getOrCreateOptimizelyUserContext = async () => {
  if (!optimizelyUserContext) {
    const client = await getReadyClient();
    // The javascript SDK client is a member of the React SDK client,
    // so the apparent redundancy below is intentional.
    optimizelyUserContext = client.client.createUserContext(
      getOrCreateOptimizelyUserIdCookie(),
      getOptimizelyUserAttributes(),
    );
  }
  return optimizelyUserContext;
};

/**
 * Use the Feature Flags API to decide whether a particular feature is
 * switched on for this user.
 * @param flag the feature to test
 * @returns {Promise<boolean>}
 */
export const isFeatureEnabled = async flag =>
  (await getOrCreateOptimizelyUserContext()).decide(flag).enabled;



// WEBPACK FOOTER //
// ./src-react/lib/optimizely/index.js